import {
  BackButton,
  Card,
  ErrorAlert,
  LoadingAlert,
  Text,
  TradeConfirmationReceipt,
  TradeConfirmationRequest,
} from '@/components';
import { useNavigation } from '@/components/providers/common/NavigationProvider';
import { useTradeableTokens } from '@/components/providers/postauth/TradeableTokensProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import AnimatedLayout from '@/components/templates/AnimatedLayout';
import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces';
import { TRADE_CARD, WALLET } from '@/utils/strings';
import {
  FC,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TradeFormDataInterface } from './interfaces/TradeInterfaces';
import TradeForm from './TradeForm';
export interface TradeCardProps {
  className?: string;
  onTokenSelectionChange?: (tokens: TokenInterface[]) => void;
  initialBuyAsset?: TokenInterface;
  isLoading?: boolean;
}
export const SCREENS = {
  INITIAL: 'trade-initial',
  CONFIRMATION: 'trade-confirmation',
  RECEIPT: 'trade-receipt',
};
const TradeCard: FC<TradeCardProps> = ({
  className,
  onTokenSelectionChange,
  initialBuyAsset,
  isLoading = false,
}) => {
  const {
    currentScreen,
    navigateBack,
    navigateBackToStart,
    canNavigateBack,
    navigateForward,
    direction,
  } = useNavigation();
  const {
    getTokensWithPositiveBalance,
    getUnderlyingYieldBearingAssetsWithPositiveBalance,
    isInitialLoading: userPortfolioAssetsInitialLoading,
    error: userPortfolioAssetsError,
    isUserAuthorized,
  } = useWalletsContext();
  const {
    getFeaturedTokens,
    initialLoading: tradeableTokensInitialLoading,
    error: tradeableTokensError,
  } = useTradeableTokens();
  const onTokenSelectionChangeRef = useRef(onTokenSelectionChange);
  onTokenSelectionChangeRef.current = onTokenSelectionChange;
  const featuredTokens = useMemo(
    () => getFeaturedTokens(),
    [getFeaturedTokens],
  );
  const { tokens, underlyingYieldBearingAssets } = useWalletsContext();
  const assetsToDisplay = useMemo(
    () => [...tokens, ...underlyingYieldBearingAssets],
    [tokens, underlyingYieldBearingAssets],
  );
  const [tradeData, setTradeData] = useState<TradeFormDataInterface>({
    sellAsset: null,
    buyAsset: null,
    amount: 0,
  });
  const resetTradeData = useCallback(() => {
    const defaultBuyAsset = initialBuyAsset || featuredTokens?.[0] || null;
    const defaultSellAsset =
      assetsToDisplay.find((asset) => asset.id !== defaultBuyAsset?.id) ||
      assetsToDisplay?.[0] ||
      null;
    setTradeData({
      sellAsset: defaultSellAsset,
      buyAsset: defaultBuyAsset,
      amount:
        (defaultSellAsset?.balance || 0) *
        APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE,
    });
  }, [assetsToDisplay, initialBuyAsset, featuredTokens]);
  useEffect(() => {
    if (
      !tradeData.sellAsset &&
      !tradeData.buyAsset &&
      assetsToDisplay.length > 0 &&
      featuredTokens.length > 0
    ) {
      resetTradeData();
    }
  }, [
    assetsToDisplay,
    featuredTokens,
    tradeData.sellAsset,
    tradeData.buyAsset,
    resetTradeData,
  ]);
  const handleTradeDataChange = useCallback(
    (data: SetStateAction<TradeFormDataInterface>) => {
      setTradeData((prev) => {
        const newTradeData = {
          ...prev,
          ...(typeof data === 'function' ? data(prev) : data),
        };
        if (newTradeData.sellAsset?.address === '0x0') {
          const asset = newTradeData.sellAsset as any;
          console.log('Native sell asset detected:', {
            id: asset.id,
            address: asset.address,
            symbol: asset.symbol,
            name: asset.name,
            defiLlamaId: asset.defiLlamaId,
            isUnderlyingYieldBearingAsset: asset.isUnderlyingYieldBearingAsset,
          });
        }
        if (newTradeData.buyAsset?.address === '0x0') {
          const asset = newTradeData.buyAsset as any;
          console.log('Native buy asset detected:', {
            id: asset.id,
            address: asset.address,
            symbol: asset.symbol,
            name: asset.name,
            defiLlamaId: asset.defiLlamaId,
            isUnderlyingYieldBearingAsset: asset.isUnderlyingYieldBearingAsset,
          });
        }
        if (
          newTradeData.sellAsset &&
          newTradeData.sellAsset.address === '0x0'
        ) {
          const asset = newTradeData.sellAsset as any;
          if (asset.id) {
            newTradeData.yieldBearingNativeAssetId = asset.id;
          }
        } else if (
          newTradeData.buyAsset &&
          newTradeData.buyAsset.address === '0x0'
        ) {
          const asset = newTradeData.buyAsset as any;
          if (asset.id) {
            newTradeData.yieldBearingNativeAssetId = asset.id;
          }
        } else {
          delete newTradeData.yieldBearingNativeAssetId;
        }
        return newTradeData;
      });
    },
    [],
  );
  const handleSuccessfulTradeReceipt = useCallback(() => {
    resetTradeData();
    navigateBackToStart();
  }, [resetTradeData, navigateBackToStart]);
  const handleNavigateBack = useCallback(() => {
    navigateBack();
  }, [navigateBack]);
  const renderHeaderLeft = useMemo(() => {
    if (currentScreen === SCREENS.INITIAL) {
      return (
        <Text
          size={APP_CONFIG.STYLES.TEXT.BIG_HEADING}
          color={APP_CONFIG.STYLES.TEXT.BIG_HEADING_COLOR}
          value={TRADE_CARD.SCREENS.TRADE.LABELS.HEADER}
        />
      );
    }
    return canNavigateBack ? (
      <BackButton onBackClick={handleNavigateBack} />
    ) : null;
  }, [currentScreen, canNavigateBack, handleNavigateBack]);
  const renderHeaderCenter = useMemo(() => {
    switch (currentScreen) {
      case SCREENS.CONFIRMATION:
        return (
          <Text
            size={APP_CONFIG.STYLES.TEXT.BIG_HEADING}
            color={APP_CONFIG.STYLES.TEXT.BIG_HEADING_COLOR}
            value={TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.LABELS.HEADER}
          />
        );
      case SCREENS.RECEIPT:
        return (
          <Text
            size={APP_CONFIG.STYLES.TEXT.BIG_HEADING}
            color={APP_CONFIG.STYLES.TEXT.BIG_HEADING_COLOR}
            value={TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.LABELS.HEADER}
          />
        );
      default:
        return null;
    }
  }, [currentScreen]);
  const renderCardBody = useMemo(() => {
    if (!isUserAuthorized) {
      return (
        <ErrorAlert
          title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
          description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
        />
      );
    }
    const initialLoading =
      userPortfolioAssetsInitialLoading || tradeableTokensInitialLoading;
    const error = userPortfolioAssetsError || tradeableTokensError;
    if (initialLoading) {
      return (
        <LoadingAlert
          title={
            TRADE_CARD.SCREENS.TRADE.MESSAGES.LOADING.WALLETS_LOADING.TITLE
          }
          description={
            TRADE_CARD.SCREENS.TRADE.MESSAGES.LOADING.WALLETS_LOADING
              .DESCRIPTION
          }
        />
      );
    }
    if (error) {
      return (
        <ErrorAlert
          title={
            TRADE_CARD.SCREENS.TRADE.MESSAGES.ERROR.WALLETS_LOADING_ERROR.TITLE
          }
          description={
            TRADE_CARD.SCREENS.TRADE.MESSAGES.ERROR.WALLETS_LOADING_ERROR
              .DESCRIPTION
          }
        />
      );
    }
    switch (currentScreen) {
      case SCREENS.INITIAL:
        return (
          <TradeForm
            tradeData={tradeData}
            setTradeData={handleTradeDataChange}
            handleSuccessfulTradeConfiguration={() =>
              navigateForward(SCREENS.CONFIRMATION)
            }
            isLoading={isLoading}
          />
        );
      case SCREENS.CONFIRMATION:
        return (
          <TradeConfirmationRequest
            tradeData={tradeData}
            handleSuccessfulTradeConfirmation={() =>
              navigateForward(SCREENS.RECEIPT)
            }
          />
        );
      case SCREENS.RECEIPT:
        return (
          <TradeConfirmationReceipt
            tradeData={tradeData}
            handleSuccessfulTradeReceipt={handleSuccessfulTradeReceipt}
          />
        );
      default:
        return null;
    }
  }, [
    currentScreen,
    tradeData,
    handleTradeDataChange,
    handleSuccessfulTradeReceipt,
  ]);
  useEffect(() => {
    if (onTokenSelectionChangeRef.current) {
      const selectedTokens: TokenInterface[] = [];
      if (tradeData.sellAsset) selectedTokens.push(tradeData.sellAsset);
      if (tradeData.buyAsset) selectedTokens.push(tradeData.buyAsset);
      onTokenSelectionChangeRef.current(selectedTokens);
    }
  }, [tradeData]);
  return (
    <Card className={className}>
      <Card.Header left={renderHeaderLeft} center={renderHeaderCenter} />
      <Card.Body>
        <AnimatedLayout uniqueKey={currentScreen} slideFrom={direction}>
          {renderCardBody}
        </AnimatedLayout>
      </Card.Body>
    </Card>
  );
};
export default memo(TradeCard);
